import React from 'react';
import { GetReportResult } from 'config/api';
import { useModal } from 'hooks/useModal';
import { formatDate, formatDateForString, formatNumber } from 'utils/formatter';
import Loading from 'components/commons/loading/Loading';
import {
  addSignCommaFormatPercentage,
  commaFormat,
  getSign,
  Sign,
  addSignCommaFormat,
} from 'components/features/new-report/utils/formatter';
import { day } from 'components/features/new-report/weekly-report/WeeklyReport';
import { NotGetAssetsInfoModal } from 'components/features/modal/NotGetAssetsInfoModal';
import { PositionItemsProps } from 'components/features/home/ProfitAndLossStuffing/ProfitAndLossStuffing';
import { yyyymmddToDate } from '../DailyReport';
import { UpdateInfoProps } from '../../components/AssetChange';
import { getSignColor } from '../../yearly-report/YearlyReport';

type DateInformationArgs = { date: Date };

const getDateInformation = ({ date }: DateInformationArgs): React.FC => () => {
  return (
    <div className="flex flex-col items-center flex-1">
      <div className="flex items-baseline mb-1 text-sm leading-tight">
        {formatDate(date, 'yyyy年')}
      </div>
      <div className="text-base font-bold">
        {formatDate(date, 'M月d日(ddd)')}
      </div>
    </div>
  );
};

const UpdateInfo = ({ datetime }: UpdateInfoProps) => {
  return (
    <div
      className="flex justify-end items-center py-1 px-4 text-gray-700"
      style={{ fontSize: '13px' }}>
      <div className="flex items-center leading-4" style={{ height: '15px' }}>
        {datetime || '---'}
        <span className="ml-1">作成</span>
      </div>
    </div>
  );
};

type PagerProps = {
  DateInformation: React.FC<Record<string, unknown>>;
};

const Pager = ({ DateInformation }: PagerProps) => {
  return (
    <div className="flex items-center justify-between py-3 px-2">
      <DateInformation />
    </div>
  );
};

type AssetChangeInformationProps = {
  totalAssetAmount: number;
  diffTotalAssetRate: number;
  diffTotalAssetAmount: number;
  assetFluctuationThresholdPer: number;
};

const AssetChangeInformation = (props: AssetChangeInformationProps) => {
  const {
    totalAssetAmount,
    diffTotalAssetRate,
    diffTotalAssetAmount,
    assetFluctuationThresholdPer,
  } = props;

  const val =
    diffTotalAssetRate > 0
      ? assetFluctuationThresholdPer
      : -assetFluctuationThresholdPer;

  const assetFluctuationThresholdPerDisp =
    Math.abs(assetFluctuationThresholdPer) <= Math.abs(diffTotalAssetRate * 100)
      ? val
      : null;

  const changeAmountPercentage =
    assetFluctuationThresholdPerDisp !== null
      ? formatNumber(assetFluctuationThresholdPerDisp, {
          withPlusSign: true,
        })
      : null;

  const assetFluctuationThresholdPerSign =
    assetFluctuationThresholdPerDisp !== null
      ? getSign(assetFluctuationThresholdPerDisp)
      : ('zero' as Sign);

  const changeAmountColor =
    assetFluctuationThresholdPerSign !== null &&
    assetFluctuationThresholdPerSign !== undefined
      ? getSignColor(assetFluctuationThresholdPerSign)
      : '';

  const assetAmountChangeSign = getSign(diffTotalAssetAmount);
  const amountColor = getSignColor(assetAmountChangeSign);

  return (
    <div className="p-4 overflow-hidden">
      <div className="flex items-center justify-between border-bluegray-200">
        <div className="text-lg font-bold">資産総額</div>
        <div className="flex items-baseline text-26 font-bold">
          {commaFormat(totalAssetAmount)}
          <div className="text-xs ml-0.5">円</div>
        </div>
      </div>
      {changeAmountPercentage !== null && changeAmountPercentage !== undefined && (
        <div className="border-bluegray-200">
          <div className="flex items-center justify-between pb-0.5">
            <div className="text-sm font-bold text-bluegray-500">
              資産総額に
              <span className={`${changeAmountColor}`}>
                <span className="ml-1 text-lg">
                  {assetFluctuationThresholdPer}%
                </span>
                以上
              </span>
              <span className="ml-1">の変動がありました</span>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="text-sm text-bluegray-500">前日比</div>
        <div className={`flex items-center text-sm ${amountColor}`}>
          {addSignCommaFormat(diffTotalAssetAmount)}
          <span className="mr-1">円</span>(
          {addSignCommaFormatPercentage(diffTotalAssetRate, {
            numberOfDecimals: 2,
          })}
          %)
        </div>
      </div>
    </div>
  );
};

export const AssetChange = ({
  report: report_,
  positions,
}: {
  report: GetReportResult;
  positions: PositionItemsProps[];
}): React.ReactElement | null => {
  const report = report_.Day;

  const [isShowModal, { hideModal, showModal }] = useModal();

  if (
    report === null ||
    report.Base.From === null ||
    report.Base.TotalAssetAmount === null ||
    report.Base.DiffTotalAssetAmount === null ||
    report.Base.DiffTotalAssetRate === null ||
    report.Base.AssetFluctuationThresholdPer === null ||
    report.Base.AssetFluctuationThresholdPer === undefined
  )
    return <></>;

  const date = report.CreatedAt
    ? yyyymmddToDate(report.Base.From.toString())
    : null;

  if (!date)
    return (
      <>
        <Loading
          loading={true}
          circularLoading={true}
          style={{
            paddingTop: '120px',
            paddingBottom: '120px',
          }}
        />
      </>
    );

  const DateInformation = getDateInformation({ date });

  const updateInfoString =
    report.CreatedAt &&
    formatDate(new Date(report.CreatedAt.replace(' ', 'T')), 'yyyy/M/d HH:mm');

  const isItemMask =
    Number(formatDateForString(report.CreatedAt, 'yyyyMMdd')) === day;

  const isAccountError = positions.some(
    item => item.status !== null && ['100', '101'].includes(item.status)
  );

  return (
    <>
      <UpdateInfo datetime={updateInfoString} />
      <Pager DateInformation={DateInformation} />
      {isAccountError ? (
        <>
          <div
            className="flex flex-row items-center mx-4 px-5 py-2 text-sm rounded-lg"
            style={{
              backgroundColor: `rgba(${0xff}, ${0xee}, ${0xf0}, 1)`,
            }}>
            <div className="icon-alert h-5 text-red pr-2"></div>
            <div className="text-bluegray-700">
              最新ではないデータがあります
            </div>
            <div
              className="ml-auto text-link cursor-pointer"
              onClick={(): void => showModal()}>
              詳細を確認
            </div>
          </div>
        </>
      ) : (
        <></>
      )}
      {!isItemMask && (
        <AssetChangeInformation
          {...{
            totalAssetAmount: report.Base.TotalAssetAmount,
            diffTotalAssetRate: report.Base.DiffTotalAssetRate,
            diffTotalAssetAmount: report.Base.DiffTotalAssetAmount,
            assetFluctuationThresholdPer:
              report.Base.AssetFluctuationThresholdPer,
          }}
        />
      )}
      <NotGetAssetsInfoModal {...{ isShow: isShowModal, hide: hideModal }} />
    </>
  );
};
